import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * IPMATDataExplorer.jsx
 *
 * IPMAT-adapted DataExplorer (from CAT DataExplorer.jsx).
 * Uses chart_builder question-grain rows.
 * Sections: QA-SA, QA-MCQ, VA-MCQ (internal keys: qa_sa, qa_mcq, va_mcq)
 * Default marks/question = 4
 */

const SECTIONS = ['QA-SA', 'QA-MCQ', 'VA-MCQ'];
const SEC_KEY_MAP = { 'QA-SA': 'qa_sa', 'QA-MCQ': 'qa_mcq', 'VA-MCQ': 'va_mcq' };
const SEC_REV_MAP = { qa_sa: 'QA-SA', qa_mcq: 'QA-MCQ', va_mcq: 'VA-MCQ' };

const ALL_SECTIONS = '__ALL_SECTIONS__';
const ALL_AREAS = '__ALL_AREAS__';
const ALL_TOPICS = '__ALL_TOPICS__';
const ALL_TYPES = '__ALL_TYPES__';

const MEASURES = [
  { id: 'attempt_rate', label: 'Attempt Rate', subtitle: 'of {total} questions', help: 'questions attempted out of total' },
  { id: 'accuracy', label: 'Accuracy', subtitle: 'of {attempted} attempted', help: 'correct answers among attempted questions' },
  { id: 'cws', label: 'C / W / S', subtitle: 'correct · wrong · skipped', help: 'outcome breakdown' },
  { id: 'score_left', label: 'Score / Lost', subtitle: 'scored vs marks lost', help: 'marks scored, and marks lost to wrong and skipped questions' },
  { id: 'time_min', label: 'Time', subtitle: 'minutes spent', help: 'time spent on questions' },
  { id: 'mpm', label: 'Marks/min', subtitle: 'marks per minute', help: 'efficiency: marks earned per minute spent' },
];

const css = `
.byc6{--ink:#111827;--muted:#667085;--border:#e5e7eb;--soft:#f8fafc;--blue:#2563eb;--green:#169b62;--red:#dc2626;--skip:#eef2f7;--skipline:#cbd5e1;font-family:'IBM Plex Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:var(--ink);font-size:13px}
.byc6 *{box-sizing:border-box}
.byc6-shell{display:grid;grid-template-columns:250px 1fr;border:1px solid var(--border);border-radius:16px;background:#fff;overflow:hidden;box-shadow:0 8px 22px rgba(15,23,42,.06)}
.byc6-left{background:var(--soft);border-right:1px solid var(--border);padding:14px;display:flex;flex-direction:column;gap:12px}
.byc6-title{font-size:16px;font-weight:850;letter-spacing:-.02em}
.byc6-sub{font-size:11px;color:var(--muted);line-height:1.4;margin-top:2px}
.byc6-field{display:flex;flex-direction:column;gap:5px}
.byc6-label{font-size:10px;font-weight:850;text-transform:uppercase;letter-spacing:.05em;color:var(--muted)}
.byc6-select{width:100%;appearance:none;border:1px solid #d1d5db;border-radius:10px;background:#fff;color:#111827;font:inherit;font-weight:750;padding:9px 32px 9px 10px;cursor:pointer;background-image:linear-gradient(45deg,transparent 50%,#667085 50%),linear-gradient(135deg,#667085 50%,transparent 50%);background-position:calc(100% - 16px) 50%,calc(100% - 11px) 50%;background-size:5px 5px,5px 5px;background-repeat:no-repeat}
.byc6-select:disabled{opacity:.5;cursor:not-allowed;background:#f3f4f6}
.byc6-help{font-size:10px;color:var(--muted);line-height:1.35}
.byc6-path{font-size:11px;color:#374151;background:#fff;border:1px solid var(--border);border-radius:12px;padding:9px;line-height:1.45}
.byc6-context{font-size:12px;font-weight:700;color:#667085;background:#f8fafc;border-left:3px solid #2563eb;padding:7px 12px;border-radius:0 8px 8px 0;margin-bottom:14px}
.byc6-crumb{background:none;border:none;color:#2563eb;cursor:pointer;font:inherit;font-weight:700;padding:0;text-decoration:underline}
.byc6-crumb:hover{color:#1d4ed8}
.byc6-main{min-width:0;overflow:hidden;padding:16px 18px 18px}
.byc6-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:14px}
.byc6-chart-title{font-size:17px;font-weight:850;letter-spacing:-.02em}
.byc6-chart-def{font-size:14px;color:#4B5563;line-height:1.5;margin-top:6px;font-weight:600}
.byc6-chart-scope{display:block;font-size:12px;color:var(--muted);font-weight:500;margin-top:2px}
.byc6-chart-sub{font-size:12px;color:var(--muted);line-height:1.45;margin-top:2px}
.byc6-pill{border:1px solid var(--border);background:var(--soft);border-radius:999px;padding:5px 9px;font-size:11px;font-weight:800;color:#475467;white-space:nowrap}
.byc6-view-toggle{display:flex;border:1px solid #d1d8e0;border-radius:999px;overflow:hidden;flex-shrink:0}
.byc6-view-toggle button{border:0;background:#fff;color:#374151;padding:6px 12px;font-size:12px;font-weight:800;cursor:pointer;font-family:inherit;white-space:nowrap}
.byc6-view-toggle button.active{background:#111827;color:#fff}
.byc6-sub-toggle{display:inline-flex;border:1px solid #d1d8e0;border-radius:999px;overflow:hidden;flex-shrink:0;margin-top:6px;margin-bottom:8px}
.byc6-sub-toggle button{border:0;background:#fff;color:#374151;padding:5px 14px;font-size:11px;font-weight:800;cursor:pointer;font-family:inherit;white-space:nowrap}
.byc6-sub-toggle button.active{background:#111827;color:#fff}
.byc6-row{display:grid;grid-template-columns:minmax(100px,180px) 1fr minmax(60px,88px);grid-template-areas:"name bar val";gap:10px;align-items:center;padding:9px 0;border-bottom:1px solid #f1f5f9}
.byc6-row:last-child{border-bottom:0}
.byc6-name{font-size:12px;font-weight:850;line-height:1.25;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;grid-area:name}
.byc6-meta{display:block;font-size:10px;color:var(--muted);font-weight:650;margin-top:2px}
.byc6-val{text-align:right;font-size:12px;font-weight:850;color:#344054;grid-area:val}
.byc6-bar,.byc6-stack{width:100%;min-width:0;grid-area:bar}
.byc6-bar{height:24px;border-radius:8px;background:#f0f4f8;overflow:hidden;display:flex}
.byc6-fill{height:100%;background:#2563eb;border-radius:0 7px 7px 0;min-width:2px}
.byc6-fill.neg{background:var(--red)}
.byc6-fill.green{background:#169b62}
.byc6-fill.red{background:#dc2626}
.byc6-fill.orange{background:#f97316}
.byc6-stack{height:24px;border-radius:8px;overflow:hidden;display:flex;background:#f3f4f6}
.byc6-seg{height:100%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:900;min-width:0;overflow:hidden}
.byc6-green{background:var(--green);color:#fff}
.byc6-red{background:var(--red);color:#fff}
.byc6-skip{background:repeating-linear-gradient(135deg,var(--skip) 0 5px,var(--skipline) 5px 7px,var(--skip) 7px 11px);color:#334155}
.byc6-score{background:var(--blue);color:#fff}
.byc6-neg{background:var(--red);color:#fff}
.byc6-leftmarks{background:repeating-linear-gradient(135deg,var(--skip) 0 5px,var(--skipline) 5px 7px,var(--skip) 7px 11px);color:#334155}
.byc6-empty{padding:24px;text-align:center;color:var(--muted);font-size:13px;background:var(--soft);border:1px dashed #cbd5e1;border-radius:12px}
.byc6-tip{position:fixed;z-index:9999;display:none;max-width:320px;white-space:pre-line;background:#111827;color:#fff;border-radius:8px;padding:8px 10px;font-size:12px;line-height:1.45;box-shadow:0 10px 24px rgba(15,23,42,.24);pointer-events:none}
.byc6-svg{width:100%;height:230px;display:block;overflow:visible}
.byc6-axis{font-size:10px;fill:#667085;font-weight:700}
.byc6-legend{display:flex;gap:12px;flex-wrap:wrap;font-size:11px;font-weight:700;color:#475467;margin:4px 0 10px}
.byc6-leg{display:flex;align-items:center;gap:5px}
.byc6-dot{width:11px;height:11px;border-radius:3px;display:inline-block}
.byc6-dot.skip{background:repeating-linear-gradient(135deg,var(--skip) 0 5px,var(--skipline) 5px 7px,var(--skip) 7px 11px)}
.byc6-filter-toggle{display:none}
.byc6-drawer-close{display:none}
.byc6-overlay{display:none}
.byc6-mobile-header{display:none}
.byc6-pie-wrap{display:flex;flex-direction:column;align-items:center;gap:16px}
.byc6-pie{flex-shrink:0}
.byc6-pie-legend{display:flex;flex-direction:row;flex-wrap:wrap;gap:12px;justify-content:center;max-width:100%}
.byc6-pie-leg{display:flex;align-items:center;gap:6px;font-size:12px;font-weight:700;padding:3px 8px;border-radius:6px;cursor:pointer;transition:background .12s;background:#f8fafc;border:1px solid #e5e7eb}
.byc6-pie-leg:hover{background:#f0f5ff}
.byc6-pie-dot{width:12px;height:12px;border-radius:3px;flex-shrink:0}
.byc6-pie-leg-label{color:#374151;white-space:nowrap}
.byc6-pie-leg-val{color:#667085;font-weight:800;white-space:nowrap}
.byc6-pie-total{font-size:12px;font-weight:800;color:#374155;margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;text-align:center;width:100%}
@media(max-width:760px){
.byc6-pie-wrap{flex-direction:column;align-items:center}
.byc6-pie-legend{min-width:0;width:100%}
.byc6-shell{grid-template-columns:1fr;position:relative;overflow-x:clip}
.byc6-mobile-header{display:flex;align-items:center;justify-content:space-between;padding:10px 12px;border-bottom:1px solid var(--border);background:#fff}
.byc6-mobile-title{font-size:13px;font-weight:800;color:var(--ink)}
.byc6-filter-toggle{display:flex;align-items:center;gap:6px;position:static;background:none;border:1px solid #d1d5db;border-radius:8px;padding:6px 10px;font-size:12px;font-weight:800;cursor:pointer;font-family:inherit}
.byc6-drawer-close{display:block;margin-bottom:12px;background:none;border:none;cursor:pointer;font-size:13px;font-weight:800;font-family:inherit;text-align:left;padding:0}
.byc6-shell{position:relative;overflow-x:clip}
.byc6-left{position:fixed;left:12px;right:12px;bottom:calc(64px + env(safe-area-inset-bottom));top:auto;width:auto;max-height:min(70dvh,460px);z-index:10000;background:#fff;box-shadow:0 12px 32px rgba(0,0,0,.18);border-radius:16px;overflow-y:auto;padding:16px;border:1px solid #e5e7eb;opacity:0;pointer-events:none;transform:translateY(12px);transition:opacity .18s ease,transform .18s ease}
.byc6-left.open{opacity:1;pointer-events:auto;transform:translateY(0)}
.byc6-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,.22);z-index:9999}
.byc6-overlay.open{display:block}
.byc6-main{width:100%;padding:0 12px 14px}
.byc6-head{position:static;top:auto;right:auto;display:flex;align-items:center;justify-content:flex-end;padding:8px 0;margin-bottom:4px;border-bottom:1px solid var(--border)}
.byc6-chart-title{display:none}
.byc6-chart-sub{display:none}
.byc6-chart-def{font-size:12px;color:var(--muted);margin-top:4px}
.byc6-context{margin-top:8px}
.byc6-row{grid-template-columns:1fr auto;grid-template-areas:"name val" "bar bar";gap:4px 8px;padding:12px 0}
.byc6-name{white-space:normal}
.byc6-val{text-align:right}
.byc6-bar,.byc6-stack{height:28px}
}
`;

function sn(value, fallback = 0) {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function ratio(a, b) { return b > 0 ? a / b : 0; }

function normalizeSectionLabel(secKey) {
  return SEC_REV_MAP[secKey] || secKey;
}

function typeLabel(type) {
  if (type === ALL_TYPES) return 'All types';
  return `Type ${type}`;
}

function measureLabel(id) {
  return MEASURES.find((m) => m.id === id)?.label || id;
}

function measureDefinition(measure) {
  const defs = {
    attempt_rate: 'How many questions you attempted out of all available questions',
    accuracy: 'Of the questions you attempted, how many you got correct',
    cws: 'Breakdown of your outcomes: correct, wrong, and skipped questions',
    score_left: 'Marks you scored, and marks lost to wrong answers and skipped questions',
    time_min: 'Total time spent on questions in this group',
    mpm: 'How many marks you earned per minute of time spent',
  };
  return defs[measure] || '';
}

function inferGroup(section, area, topic) {
  if (section === ALL_SECTIONS) return 'section';
  if (area === ALL_AREAS) return 'area';
  return 'topic';
}

function makeRowKey(row) {
  return [
    row.test_id ?? row.test_name ?? 'test',
    row.question_id ?? row.qid ?? row.question_no ?? Math.random(),
    row.section, row.area, row.topic, row.type,
  ].join('|');
}

function normalizeQuestionRow(row, idx) {
  const attempted = sn(row.attempted);
  const correct = sn(row.correct);
  const wrong = sn(row.wrong);
  const total = sn(row.total_questions, 1);
  const skipped = row.skipped === undefined ? Math.max(0, total - attempted) : sn(row.skipped);
  const marksAvailable = sn(row.marks_available, total * 4); // IPMAT default 4 marks/question
  const rawScore = sn(row.marks_obtained ?? row.score);
  const timeSec = row.time_sec !== undefined ? sn(row.time_sec) : sn(row.time_min) * 60;
  const timeMin = timeSec > 0 ? timeSec / 60 : sn(row.time_min, 0);

  return {
    id: row.id || makeRowKey(row) || `row-${idx}`,
    test_id: row.test_id || null,
    test_name: row.test_name || 'Test',
    test_order: sn(row.test_order, 999),
    is_current: !!row.is_current || sn(row.test_order, 999) === 0,
    section: normalizeSectionLabel(String(row.section || '')),
    area: row.area || '',
    topic: row.topic || '',
    type: String(row.type || 'All').replace('Type ', '').toUpperCase(),
    total_questions: total,
    attempted, correct, wrong, skipped,
    marks_obtained: rawScore,
    marks_available: marksAvailable,
    time_sec: timeSec,
    time_min: timeMin,
  };
}

function normalizeSectionHistory(row, idx) {
  return {
    id: row.id || `${row.section}-${row.test_id || row.test_name || idx}`,
    section: normalizeSectionLabel(String(row.section || '')),
    test_id: row.test_id || null,
    test_name: row.test_name || 'Test',
    test_order: sn(row.test_order, 999),
    is_current: !!row.is_current || sn(row.test_order, 999) === 0,
    score: sn(row.score ?? row.marks_obtained, null),
    total_marks: sn(row.total_marks ?? row.marks_available, null),
  };
}

function aggregateRows(rows, groupBy) {
  const map = new Map();
  rows.forEach((r) => {
    const key = groupBy === 'section' ? r.section : groupBy === 'area' ? r.area : r.topic;
    if (!key) return;
    if (!map.has(key)) {
      map.set(key, {
        key, label: key, fullLabel: key, rows: [],
        total_questions: 0, attempted: 0, correct: 0, wrong: 0, skipped: 0,
        marks_obtained: 0, marks_available: 0, time_sec: 0,
      });
    }
    const g = map.get(key);
    g.rows.push(r);
    g.total_questions += sn(r.total_questions);
    g.attempted += sn(r.attempted);
    g.correct += sn(r.correct);
    g.wrong += sn(r.wrong);
    g.skipped += sn(r.skipped);
    g.marks_obtained += sn(r.marks_obtained);
    g.marks_available += sn(r.marks_available);
    g.time_sec += sn(r.time_sec);
  });

  return Array.from(map.values()).map((g) => {
    const timeMin = g.time_sec > 0 ? g.time_sec / 60 : 0;
    return {
      ...g,
      marks_left: Math.max(0, g.marks_available - g.marks_obtained),
      attempt_rate: ratio(g.attempted, g.total_questions) * 100,
      accuracy: ratio(g.correct, g.correct + g.wrong) * 100,
      conversion: ratio(g.correct, g.total_questions) * 100,
      time_min: timeMin,
      mpm: timeMin > 0 ? g.marks_obtained / timeMin : null,
    };
  });
}

function valueFor(row, measure) {
  if (measure === 'score_left') return row.marks_obtained;
  return row[measure];
}

function formatValue(v, measure) {
  if (!Number.isFinite(v)) return '—';
  if (['attempt_rate', 'accuracy'].includes(measure)) return `${v.toFixed(1)}%`;
  if (measure === 'time_min') return `${v.toFixed(1)} min`;
  if (measure === 'mpm') return v.toFixed(2);
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function filterQuestionRows(rows, { section, area, topic, type }) {
  return rows.filter((r) => {
    if (section !== ALL_SECTIONS && r.section !== section) return false;
    if (area !== ALL_AREAS && r.area !== area) return false;
    if (topic !== ALL_TOPICS && r.topic !== topic) return false;
    if (type !== ALL_TYPES && r.type !== type) return false;
    return true;
  });
}

function optionsFromRows(rows, field, filters = {}) {
  const set = new Set();
  rows.forEach((r) => {
    if (filters.section && filters.section !== ALL_SECTIONS && r.section !== filters.section) return;
    if (filters.area && filters.area !== ALL_AREAS && r.area !== filters.area) return;
    if (filters.topic && filters.topic !== ALL_TOPICS && r.topic !== filters.topic) return;
    const value = r[field];
    if (value) set.add(value);
  });
  return Array.from(set).sort();
}

function sortRows(rows, measure) {
  if (measure === 'cws') return [...rows].sort((a, b) => b.total_questions - a.total_questions);
  return [...rows].sort((a, b) => sn(valueFor(b, measure), -Infinity) - sn(valueFor(a, measure), -Infinity));
}

function measureColor(measure) {
  if (measure === 'mpm') return 'green';
  return '';
}

// ── Tooltip helpers ──

function rowTooltip(r, measure) {
  switch (measure) {
    case 'attempt_rate': {
      const attempted = sn(r.attempted), total = sn(r.total_questions), skipped = total - attempted;
      const lines = [r.label, `${attempted}/${total} attempted`];
      if (skipped > 0) lines.push(`${skipped} skipped`);
      lines.push(`Attempt Rate: ${formatValue(r.attempt_rate, 'attempt_rate')}`);
      return lines.join('\n');
    }
    case 'accuracy': {
      const attempted = Math.max(1, sn(r.attempted));
      return [r.label, `${sn(r.correct)} correct of ${attempted} attempted`, `${sn(r.wrong)} wrong`, `Accuracy: ${formatValue(r.accuracy, 'accuracy')}`].join('\n');
    }
    case 'cws':
      return [r.label, `${sn(r.total_questions)} questions`, `${sn(r.correct)}C · ${sn(r.wrong)}W · ${sn(r.skipped)}S`, `Conversion: ${sn(r.conversion).toFixed(1)}%`].join('\n');
    case 'score_left': {
      const lost = Math.max(0, sn(r.marks_available) - sn(r.marks_obtained));
      return [r.label, `Scored: ${sn(r.marks_obtained).toFixed(1)} of ${sn(r.marks_available).toFixed(0)} available`, `Lost: ${lost.toFixed(1)} marks — ${sn(r.wrong)} wrong, ${sn(r.skipped)} skipped`].join('\n');
    }
    case 'time_min': {
      const total = sn(r.total_questions), mins = sn(r.time_min);
      return [r.label, `${mins.toFixed(1)} min total`, `${total > 0 ? (mins / total).toFixed(1) : '—'} min/question`].join('\n');
    }
    case 'mpm':
      return [r.label, `${sn(r.marks_obtained).toFixed(1)} marks in ${sn(r.time_min).toFixed(1)} min`, `${formatValue(r.mpm, 'mpm')} marks/min`].join('\n');
    default: return r.label;
  }
}

function buildSparklineData(questionRows, groupBy, measure) {
  const sparklines = {};
  questionRows.forEach((r) => {
    const key = groupBy === 'section' ? r.section : groupBy === 'area' ? r.area : r.topic;
    if (!key) return;
    if (!sparklines[key]) sparklines[key] = {};
    if (!sparklines[key][r.test_order]) {
      sparklines[key][r.test_order] = {
        test_order: r.test_order, test_name: r.test_name,
        total_questions: 0, attempted: 0, correct: 0, wrong: 0, skipped: 0,
        marks_obtained: 0, marks_available: 0, time_sec: 0,
      };
    }
    const g = sparklines[key][r.test_order];
    g.total_questions += sn(r.total_questions); g.attempted += sn(r.attempted);
    g.correct += sn(r.correct); g.wrong += sn(r.wrong); g.skipped += sn(r.skipped);
    g.marks_obtained += sn(r.marks_obtained); g.marks_available += sn(r.marks_available);
    g.time_sec += sn(r.time_sec);
  });

  const result = {};
  Object.keys(sparklines).forEach((key) => {
    const points = Object.values(sparklines[key])
      .sort((a, b) => b.test_order - a.test_order)
      .map((g) => {
        const timeMin = g.time_sec > 0 ? g.time_sec / 60 : 0;
        let value;
        switch (measure) {
          case 'attempt_rate': value = ratio(g.attempted, g.total_questions) * 100; break;
          case 'accuracy': value = ratio(g.correct, g.correct + g.wrong) * 100; break;
          case 'time_min': value = timeMin; break;
          case 'mpm': value = timeMin > 0 ? g.marks_obtained / timeMin : null; break;
          case 'score_left': value = g.marks_obtained; break;
          default: value = null;
        }
        return {
          test_order: g.test_order, test_name: g.test_name,
          value: Number.isFinite(value) ? value : null,
          total_questions: g.total_questions, attempted: g.attempted,
          correct: g.correct, wrong: g.wrong, skipped: g.skipped,
          marks_obtained: g.marks_obtained, marks_available: g.marks_available, time_min: timeMin,
        };
      })
      .filter((p) => p.value !== null);
    result[key] = points;
  });
  return result;
}

function trendRowTooltip(r, measure, sparkPoints) {
  if (!sparkPoints || sparkPoints.length === 0) return rowTooltip(r, measure);
  const values = sparkPoints.map((p) => p.value).filter(Number.isFinite);
  if (values.length === 0) return r.label;
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const latest = values[values.length - 1];
  const prev = values.length >= 2 ? values[values.length - 2] : null;
  const latestPt = sparkPoints[sparkPoints.length - 1];
  const lines = [r.label];
  switch (measure) {
    case 'attempt_rate':
      lines.push(`Latest: ${formatValue(latest, measure)} (${sn(latestPt.attempted)}/${sn(latestPt.total_questions)} attempted)`); break;
    case 'accuracy':
      lines.push(`Latest: ${formatValue(latest, measure)} (${sn(latestPt.correct)}/${sn(latestPt.correct) + sn(latestPt.wrong)} correct)`); break;
    case 'mpm':
      lines.push(`Latest: ${formatValue(latest, measure)} (${sn(latestPt.marks_obtained).toFixed(1)} marks in ${sn(latestPt.time_min).toFixed(1)} min)`); break;
    default:
      lines.push(`Latest: ${formatValue(latest, measure)}`);
  }
  lines.push(`Avg: ${formatValue(avg, measure)} across ${values.length} tests`);
  const deltaVsAvg = latest - avg;
  const arrow = deltaVsAvg > 0 ? '↑' : deltaVsAvg < 0 ? '↓' : '→';
  lines.push(`vs avg: ${arrow} ${deltaVsAvg >= 0 ? '+' : ''}${deltaVsAvg.toFixed(1)}`);
  if (prev !== null) {
    const d = latest - prev;
    lines.push(`vs previous: ${d > 0 ? '↑' : d < 0 ? '↓' : '→'} ${d >= 0 ? '+' : ''}${d.toFixed(1)}`);
  }
  return lines.join('\n');
}

function pointTooltip(p, measure) {
  const lines = [p.test_name || 'Test'];
  switch (measure) {
    case 'attempt_rate': lines.push(formatValue(p.value, measure), `${sn(p.attempted)}/${sn(p.total_questions)} attempted`); break;
    case 'accuracy': lines.push(formatValue(p.value, measure), `${sn(p.correct)} correct of ${sn(p.correct) + sn(p.wrong)} attempted`); break;
    case 'mpm': lines.push(`${formatValue(p.value, measure)} marks/min`, `${sn(p.marks_obtained).toFixed(1)} marks in ${sn(p.time_min).toFixed(1)} min`); break;
    default: lines.push(formatValue(p.value, measure));
  }
  return lines.join('\n');
}

function trendArrowLabel(sparkPoints) {
  if (!sparkPoints || sparkPoints.length === 0) return null;
  const values = sparkPoints.map((p) => p.value).filter(Number.isFinite);
  if (values.length === 0) return null;
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const latest = values[values.length - 1];
  const delta = latest - avg;
  const arrow = delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
  const color = delta > 0 ? '#169b62' : delta < 0 ? '#dc2626' : '#667085';
  return <span style={{ color, fontSize: 13, marginLeft: 4 }}>{arrow} {delta > 0 ? '+' : ''}{delta.toFixed(1)}</span>;
}

// ── SparklineBar ──
function SparklineBar({ points, measure, showTip, hideTip }) {
  if (!points || points.length < 1) return <div className="byc6-bar"><div className="byc6-fill" style={{ width: '0%' }} /></div>;
  const w = 300, h = 28, padX = 8, padY = 5, innerW = w - padX * 2, innerH = h - padY * 2;
  const values = points.map(p => p.value).filter(v => Number.isFinite(v));
  if (values.length === 0) return <div className="byc6-bar" />;
  const min = Math.min(...values), max = Math.max(...values), range = max - min || 1;
  const coords = points.map((p, i) => {
    if (!Number.isFinite(p.value)) return null;
    const x = points.length === 1 ? w / 2 : padX + (i / (points.length - 1)) * innerW;
    const y = padY + (1 - (p.value - min) / range) * innerH;
    return { x, y, point: p };
  }).filter(Boolean);
  const pathD = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: '28px', display: 'block' }}>
      <path d={pathD} fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map((c, i) => {
        const dh = showTip ? {
          onMouseEnter: (e) => { e.stopPropagation(); showTip(pointTooltip(c.point, measure), e); },
          onMouseMove: (e) => { e.stopPropagation(); showTip(pointTooltip(c.point, measure), e); },
          onClick: (e) => { e.stopPropagation(); showTip(pointTooltip(c.point, measure), e); },
          onMouseLeave: (e) => { e.stopPropagation(); hideTip(); },
        } : {};
        return (<g key={i}><circle cx={c.x} cy={c.y} r="10" fill="transparent" style={{ cursor: showTip ? 'pointer' : 'default' }} {...dh} /><circle cx={c.x} cy={c.y} r="4" fill="#2563eb" pointerEvents="none" /></g>);
      })}
    </svg>
  );
}

// ── BarRows ──
function BarRows({ rows, measure, showTip, hideTip, view, sparklineData }) {
  const isPct = measure === 'attempt_rate';
  const max = isPct ? 100 : Math.max(...rows.map(r => Math.abs(sn(valueFor(r, measure)))), 1);
  const cc = measureColor(measure);
  return (<div>{rows.map(r => {
    const val = valueFor(r, measure), bw = Math.max(3, Math.abs(sn(val)) / max * 100);
    const sp = view === 'trend' ? sparklineData?.[r.key] : null;
    const tip = sp ? trendRowTooltip(r, measure, sp) : rowTooltip(r, measure);
    const rH = (!sp && showTip) ? { onMouseEnter: e => showTip(tip, e), onMouseMove: e => showTip(tip, e), onMouseLeave: hideTip, onClick: e => showTip(tip, e) } : {};
    const lH = (sp && showTip) ? { onMouseEnter: e => showTip(tip, e), onMouseMove: e => showTip(tip, e), onMouseLeave: hideTip, onClick: e => showTip(tip, e) } : {};
    const mt = measure === 'attempt_rate' ? `${r.total_questions}q` : measure === 'time_min' ? `${r.time_min.toFixed(1)} min` : `${r.total_questions}q`;
    const ta = sp ? trendArrowLabel(sp) : null;
    return (<div key={r.key} className="byc6-row" {...rH}>
      <div className="byc6-name" {...lH}>{r.label}<span className="byc6-meta">{mt}</span></div>
      {sp ? <SparklineBar points={sp} measure={measure} showTip={showTip} hideTip={hideTip} /> : <div className="byc6-bar"><div className={`byc6-fill ${sn(val) < 0 ? 'neg' : ''} ${cc}`} style={{ width: `${bw}%` }} /></div>}
      <div className="byc6-val" {...lH}>{sp ? null : formatValue(val, measure)}{ta}</div>
    </div>);
  })}</div>);
}

function OutcomeLegend() {
  return (<div className="byc6-legend">
    <span className="byc6-leg"><span className="byc6-dot" style={{ background: 'var(--green)' }} />Correct</span>
    <span className="byc6-leg"><span className="byc6-dot" style={{ background: 'var(--red)' }} />Wrong</span>
    <span className="byc6-leg"><span className="byc6-dot skip" />Skipped</span>
  </div>);
}

function StackedRows({ rows, showTip, hideTip }) {
  return (<div>{rows.map(r => {
    const t = Math.max(1, r.correct + r.wrong + r.skipped);
    const tip = rowTooltip(r, 'cws');
    const h = showTip ? { onMouseEnter: e => showTip(tip, e), onMouseMove: e => showTip(tip, e), onMouseLeave: hideTip, onClick: e => showTip(tip, e) } : {};
    return (<div key={r.key} className="byc6-row" {...h}>
      <div className="byc6-name">{r.label}<span className="byc6-meta">{t}q</span></div>
      <div className="byc6-stack">
        {r.correct > 0 && <div className="byc6-seg byc6-green" style={{ width: `${ratio(r.correct, t) * 100}%` }}>{r.correct}</div>}
        {r.wrong > 0 && <div className="byc6-seg byc6-red" style={{ width: `${ratio(r.wrong, t) * 100}%` }}>{r.wrong}</div>}
        {r.skipped > 0 && <div className="byc6-seg byc6-skip" style={{ width: `${ratio(r.skipped, t) * 100}%` }}>{r.skipped}</div>}
      </div>
      <div className="byc6-val">{r.correct}C {r.wrong}W {r.skipped}S</div>
    </div>);
  })}</div>);
}

function AccuracyRows({ rows, showTip, hideTip, view, sparklineData }) {
  return (<div>{rows.map(r => {
    const att = Math.max(1, r.attempted), cP = ratio(r.correct, att) * 100, wP = ratio(r.wrong, att) * 100;
    const sp = view === 'trend' ? sparklineData?.[r.key] : null;
    const tip = sp ? trendRowTooltip(r, 'accuracy', sp) : rowTooltip(r, 'accuracy');
    const rH = (!sp && showTip) ? { onMouseEnter: e => showTip(tip, e), onMouseMove: e => showTip(tip, e), onMouseLeave: hideTip, onClick: e => showTip(tip, e) } : {};
    const lH = (sp && showTip) ? { onMouseEnter: e => showTip(tip, e), onMouseMove: e => showTip(tip, e), onMouseLeave: hideTip, onClick: e => showTip(tip, e) } : {};
    const ta = sp ? trendArrowLabel(sp) : null;
    return (<div key={r.key} className="byc6-row" {...rH}>
      <div className="byc6-name" {...lH}>{r.label}<span className="byc6-meta">of {r.attempted} attempted</span></div>
      {sp ? <SparklineBar points={sp} measure="accuracy" showTip={showTip} hideTip={hideTip} /> : (<div className="byc6-stack">{r.correct > 0 && <div className="byc6-seg byc6-green" style={{ width: `${cP}%` }}>{r.correct}</div>}{r.wrong > 0 && <div className="byc6-seg byc6-red" style={{ width: `${wP}%` }}>{r.wrong}</div>}</div>)}
      <div className="byc6-val" {...lH}>{sp ? null : formatValue(r.accuracy, 'accuracy')}{ta}</div>
    </div>);
  })}</div>);
}

function ScoreLeftRows({ rows, showTip, hideTip }) {
  const max = Math.max(...rows.map(r => sn(r.marks_available)), 1);
  return (<div>{rows.map(r => {
    const t = Math.max(1, sn(r.marks_available)), bw = Math.max(8, t / max * 100);
    const score = sn(r.marks_obtained), isNeg = score < 0;
    const sP = ratio(Math.abs(score), t) * 100, lP = ratio(Math.min(Math.max(0, r.marks_left), t), t) * 100;
    const tip = rowTooltip(r, 'score_left');
    const h = showTip ? { onMouseEnter: e => showTip(tip, e), onMouseMove: e => showTip(tip, e), onMouseLeave: hideTip, onClick: e => showTip(tip, e) } : {};
    return (<div key={r.key} className="byc6-row" {...h}>
      <div className="byc6-name">{r.label}<span className="byc6-meta">score {score.toFixed(1)}</span></div>
      <div className="byc6-stack" style={{ width: `${bw}%` }}>
        {isNeg ? <div className="byc6-seg byc6-neg" style={{ width: `${sP}%` }}>{score}</div>
          : <>{sP > 0 && <div className="byc6-seg byc6-score" style={{ width: `${sP}%` }}>{score}</div>}{lP > 0 && <div className="byc6-seg byc6-leftmarks" style={{ width: `${lP}%` }}>{sn(r.marks_left).toFixed(1)}</div>}</>}
      </div>
      <div className="byc6-val">{score}/{sn(r.marks_available)}</div>
    </div>);
  })}</div>);
}

function TimeRows({ rows, showTip, hideTip }) {
  const total = rows.reduce((s, r) => s + sn(r.time_min), 0), mx = Math.max(...rows.map(r => sn(r.time_min)), 1);
  return (<div>{[...rows].sort((a, b) => sn(b.time_min) - sn(a.time_min)).map(r => {
    const m = sn(r.time_min), p = total > 0 ? m / total * 100 : 0, bw = Math.max(3, m / mx * 100);
    const tip = `${r.label}\n${m.toFixed(1)} min`; const h = showTip ? { onMouseEnter: e => showTip(tip, e), onMouseMove: e => showTip(tip, e), onMouseLeave: hideTip, onClick: e => showTip(tip, e) } : {};
    return (<div key={r.key} className="byc6-row" {...h}><div className="byc6-name">{r.label}<span className="byc6-meta">{m.toFixed(1)} min</span></div><div className="byc6-bar"><div className="byc6-fill" style={{ width: `${bw}%` }} /></div><div className="byc6-val">{m.toFixed(1)} min</div></div>);
  })}<div style={{ fontSize: 13, fontWeight: 800, padding: '8px 0', borderTop: '1px solid #e5e7eb', marginTop: 4 }}>Total: {total.toFixed(1)} min</div></div>);
}

// ── Main Component ──

export default function IPMATDataExplorer({ stats }) {
  const chartBuilder = stats?.chart_builder || {};
  const [measure, setMeasure] = useState('attempt_rate');
  const [view, setView] = useState('current');
  const [trendView, setTrendView] = useState('line');
  const [section, setSection] = useState(ALL_SECTIONS);
  const [area, setArea] = useState(ALL_AREAS);
  const [topic, setTopic] = useState(ALL_TOPICS);
  const [type, setType] = useState(ALL_TYPES);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const tipRef = useRef(null);

  const showTip = useCallback((text, e) => {
    const tip = tipRef.current;
    if (!tip) return;
    tip.textContent = text; tip.style.display = 'block';
    const rect = tip.getBoundingClientRect();
    const baseX = e.clientX || 0, baseY = e.clientY || 0;
    let x = baseX + 12, y = baseY + 12;
    if (x + rect.width > window.innerWidth - 12) x = baseX - rect.width - 12;
    if (y + rect.height > window.innerHeight - 12) y = baseY - rect.height - 12;
    tip.style.left = `${Math.max(8, x)}px`; tip.style.top = `${Math.max(8, y)}px`;
  }, []);
  const hideTip = useCallback(() => { if (tipRef.current) tipRef.current.style.display = 'none'; }, []);

  const questionRows = useMemo(() => {
    const src = view === 'current' ? chartBuilder.current : chartBuilder.trend;
    return Array.isArray(src) ? src.map(normalizeQuestionRow) : [];
  }, [chartBuilder.current, chartBuilder.trend, view]);

  const groupBy = inferGroup(section, area, topic);
  const measureObj = MEASURES.find(m => m.id === measure);

  const sectionOptions = useMemo(() => {
    const secs = optionsFromRows(questionRows, 'section');
    return secs.length ? secs : SECTIONS;
  }, [questionRows]);

  const areaOptions = useMemo(() => {
    if (section === ALL_SECTIONS) return [];
    return optionsFromRows(questionRows, 'area', { section });
  }, [questionRows, section]);

  const topicOptions = useMemo(() => {
    if (section === ALL_SECTIONS || area === ALL_AREAS) return [];
    return optionsFromRows(questionRows, 'topic', { section, area });
  }, [questionRows, section, area]);

  const typeOptions = useMemo(() => {
    return optionsFromRows(questionRows, 'type', { section, area, topic }).filter(t => ['A', 'B', 'C'].includes(t));
  }, [questionRows, section, area, topic]);

  useEffect(() => { if (section === ALL_SECTIONS) { setArea(ALL_AREAS); setTopic(ALL_TOPICS); } }, [section]);
  useEffect(() => { if (area === ALL_AREAS) setTopic(ALL_TOPICS); }, [area]);

  const filteredQuestions = useMemo(() => filterQuestionRows(questionRows, { section, area, topic, type }), [questionRows, section, area, topic, type]);
  const aggregatedRows = useMemo(() => sortRows(aggregateRows(filteredQuestions, groupBy), measure).slice(0, 15), [filteredQuestions, groupBy, measure]);
  const sparklineData = useMemo(() => { if (view !== 'trend') return {}; return buildSparklineData(filteredQuestions, groupBy, measure); }, [filteredQuestions, groupBy, measure, view]);

  const pathLabel = [
    section === ALL_SECTIONS ? 'All sections' : section,
    section !== ALL_SECTIONS ? (area === ALL_AREAS ? 'All areas' : area) : null,
    section !== ALL_SECTIONS && area !== ALL_AREAS ? (topic === ALL_TOPICS ? 'All topics' : topic) : null,
  ].filter(Boolean).join(' \u2192 ');

  const chartTitle = `${measureLabel(measure)} of ${pathLabel}`;
  const missingBackend = !chartBuilder.current || !chartBuilder.trend;

  return (
    <div className="byc6">
      <style>{css}</style>
      <div className="byc6-shell">
        <div className="byc6-mobile-header">
          <button className="byc6-filter-toggle" onClick={() => setDrawerOpen(true)}>{'\u2630'} Filters</button>
          <span className="byc6-mobile-title">Data Explorer</span>
        </div>
        <div className={`byc6-overlay ${drawerOpen ? 'open' : ''}`} onClick={() => setDrawerOpen(false)} />
        <aside className={`byc6-left ${drawerOpen ? 'open' : ''}`}>
          <button className="byc6-drawer-close" onClick={() => setDrawerOpen(false)}>{'\u2715'} Close</button>
          <div><div className="byc6-title">Data Explorer</div><div className="byc6-sub">Explore your IPMAT performance data.</div></div>
          <div className="byc6-field">
            <label className="byc6-label">Measure</label>
            <select className="byc6-select" value={measure} onChange={e => setMeasure(e.target.value)}>
              {MEASURES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <div className="byc6-help">{measureObj?.help}</div>
          </div>
          <div className="byc6-field">
            <label className="byc6-label">Of</label>
            <select className="byc6-select" value={section} onChange={e => setSection(e.target.value)}>
              <option value={ALL_SECTIONS}>All sections</option>
              {sectionOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {section !== ALL_SECTIONS && (
            <div className="byc6-field">
              <label className="byc6-label">Area</label>
              <select className="byc6-select" value={area} onChange={e => setArea(e.target.value)}>
                <option value={ALL_AREAS}>All {section} areas</option>
                {areaOptions.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          )}
          {section !== ALL_SECTIONS && area !== ALL_AREAS && (
            <div className="byc6-field">
              <label className="byc6-label">Topic</label>
              <select className="byc6-select" value={topic} onChange={e => setTopic(e.target.value)}>
                <option value={ALL_TOPICS}>All topics</option>
                {topicOptions.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          <div className="byc6-field">
            <label className="byc6-label">Question type</label>
            <select className="byc6-select" value={type} onChange={e => setType(e.target.value)}>
              <option value={ALL_TYPES}>All types</option>
              {typeOptions.map(t => <option key={t} value={t}>Type {t}</option>)}
            </select>
          </div>
        </aside>
        <main className="byc6-main">
          <div className="byc6-context">
            {measureLabel(measure)} {'\u00b7'} {section === ALL_SECTIONS ? 'All sections' : <>{section}{area !== ALL_AREAS ? ` \u203a ${area}` : ''}{topic !== ALL_TOPICS ? ` \u203a ${topic}` : ''}</>} {'\u00b7'} {typeLabel(type)}
          </div>
          <div className="byc6-head">
            <div>
              <div className="byc6-chart-title">{chartTitle}</div>
              <div className="byc6-chart-def">
                {measureDefinition(measure)}
                {view === 'trend' && <span className="byc6-chart-scope">Showing current test + up to 5 past tests</span>}
              </div>
            </div>
            <div className="byc6-view-toggle">
              <button className={view === 'current' ? 'active' : ''} onClick={() => setView('current')}>Current Test</button>
              <button className={view === 'trend' ? 'active' : ''} onClick={() => setView('trend')}>Trend</button>
            </div>
          </div>
          {missingBackend ? (
            <div className="byc6-empty">This data is not available for this report. Try viewing reports generated for newer tests.</div>
          ) : aggregatedRows.length === 0 ? (
            <div className="byc6-empty">No chart data available for this selection.</div>
          ) : (view === 'trend' && ['attempt_rate', 'accuracy', 'mpm'].includes(measure)) ? (
            <>
              <div className="byc6-sub-toggle" style={{ marginBottom: 12 }}>
                <button className={trendView === 'line' ? 'active' : ''} onClick={() => setTrendView('line')}>Trendline</button>
                <button className={trendView === 'bar' ? 'active' : ''} onClick={() => setTrendView('bar')}>Cumulative</button>
              </div>
              {(() => {
                const v = trendView === 'line' ? 'trend' : 'current';
                const sd = trendView === 'line' ? sparklineData : null;
                if (measure === 'accuracy') return <><OutcomeLegend /><AccuracyRows rows={aggregatedRows} showTip={showTip} hideTip={hideTip} view={v} sparklineData={sd} /></>;
                return <BarRows rows={aggregatedRows} measure={measure} showTip={showTip} hideTip={hideTip} view={v} sparklineData={sd} />;
              })()}
            </>
          ) : measure === 'cws' ? (
            <><OutcomeLegend /><StackedRows rows={aggregatedRows} showTip={showTip} hideTip={hideTip} /></>
          ) : measure === 'accuracy' ? (
            <><OutcomeLegend /><AccuracyRows rows={aggregatedRows} showTip={showTip} hideTip={hideTip} view={view} sparklineData={sparklineData} /></>
          ) : measure === 'score_left' ? (
            <ScoreLeftRows rows={aggregatedRows} showTip={showTip} hideTip={hideTip} />
          ) : measure === 'time_min' ? (
            <TimeRows rows={aggregatedRows} showTip={showTip} hideTip={hideTip} />
          ) : (
            <BarRows rows={aggregatedRows} measure={measure} showTip={showTip} hideTip={hideTip} view={view} sparklineData={sparklineData} />
          )}
        </main>
      </div>
      <div className="byc6-tip" ref={tipRef} />
    </div>
  );
}
